<template>
  <div class="profile-page">
    <div class="page-container">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('profile.title') }}</h1>
        <p class="page-subtitle">{{ $t('profile.subtitle') }}</p>
      </div>

      <!-- User Information Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.userInfo') }}</h2>
        </div>
        <div class="card-content">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">{{ $t('profile.name') }}</label>
              <p class="info-value">{{ user?.name || $t('profile.notSet') }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.email') }}</label>
              <p class="info-value">{{ user?.email || $t('profile.notSet') }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.memberSince') }}</label>
              <p class="info-value">{{ formatDate(user?.created_at) }}</p>
            </div>
            <div class="info-item">
              <label class="info-label">{{ $t('profile.language') }}</label>
              <div class="language-selector-wrapper">
                <LanguageSelector />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscription Info Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.subscription') }}</h2>
          <router-link to="/subscription" class="btn-secondary">
            {{ $t('profile.manageSubscription') }}
          </router-link>
        </div>
        <div class="card-content">
          <div class="subscription-info">
            <div class="sub-item">
              <label class="sub-label">{{ $t('profile.plan') }}</label>
              <p class="sub-value">{{ planName }}</p>
            </div>
            <div class="sub-item">
              <label class="sub-label">{{ $t('profile.scansThisMonth') }}</label>
              <p class="sub-value">{{ scansUsed }} / {{ scanLimit }}</p>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: `${usagePercentage}%` }"></div>
              </div>
            </div>
            <div v-if="hardScanLimit > 0" class="sub-item">
              <label class="sub-label">{{ $t('profile.hardScans') }}</label>
              <p class="sub-value">{{ hardScansUsed }} / {{ hardScanLimit }}</p>
              <div class="progress-bar">
                <div class="progress-fill progress-fill-premium" :style="{ width: `${hardScanUsagePercentage}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- API Keys Card -->
      <div class="section-card">
        <div class="card-header">
          <h2 class="card-title">{{ $t('profile.apiKeys') }}</h2>
          <button @click="showCreateKeyModal = true" class="btn-primary">
            <font-awesome-icon icon="fa-solid fa-plus" />
            {{ $t('profile.createNewKey') }}
          </button>
        </div>
        <div class="card-content">
          <div class="api-note">
            <font-awesome-icon icon="fa-solid fa-circle-info" style="font-size: 20px;" />
            <div>
              <p class="note-title">API Key Information</p>
              <p class="note-text">
                API keys allow external applications to access your account. Keep them secure and never share them publicly.
                Use the <code>/api/invoices-api</code> endpoint with the <code>X-API-Key</code> header.
              </p>
            </div>
          </div>

          <!-- API Keys List -->
          <div v-if="apiKeys.length > 0" class="api-keys-list">
            <div v-for="key in apiKeys" :key="key.id" class="api-key-item">
              <div class="key-info">
                <div class="key-name">{{ key.name }}</div>
                <div class="key-value">{{ key.masked_key }}</div>
                <div class="key-meta">Created {{ formatDate(key.created_at) }}</div>
              </div>
              <div class="key-actions">
                <button @click="deleteApiKey(key.id)" class="btn-delete">
                  <font-awesome-icon icon="fa-solid fa-trash" />
                  Delete
                </button>
              </div>
            </div>
          </div>
          <div v-else class="no-keys">
            <p>No API keys yet. Create one to get started with the API.</p>
          </div>

          <div class="api-example">
            <p class="example-title">Example Usage:</p>
            <pre class="example-code">curl -X POST {{ appDomain }}/api/invoices-api -H "X-API-Key: your_api_key_here" -F "file=@invoice.pdf"</pre>
          </div>
        </div>
      </div>

      <!-- Create API Key Modal -->
      <div v-if="showCreateKeyModal" class="modal-overlay" @click="showCreateKeyModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Create New API Key</h3>
            <button @click="showCreateKeyModal = false" class="btn-close">×</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Key Name</label>
              <input
                v-model="newKeyName"
                type="text"
                placeholder="e.g., Production Server"
                class="form-input"
              />
            </div>
            <div v-if="newCreatedKey" class="key-created-alert">
              <p class="alert-title">⚠️ Save this key now!</p>
              <p class="alert-text">You won't be able to see it again.</p>
              <div class="key-display">
                <code>{{ newCreatedKey }}</code>
                <button @click="copyKey" class="btn-copy">
                  {{ keyCopied ? 'Copied!' : 'Copy' }}
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showCreateKeyModal = false" class="btn-secondary">Close</button>
            <button v-if="!newCreatedKey" @click="createApiKey" class="btn-primary">Create Key</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useScanStore } from '@/stores/scan';
import { useUiStore } from '@/stores/ui';
import api from '@/utils/api';
import LanguageSelector from '@/components/LanguageSelector.vue';

const authStore = useAuthStore();
const scanStore = useScanStore();
const uiStore = useUiStore();
const appDomain = window.location.origin;

const user = computed(() => authStore.user);
const subscription = computed(() => authStore.subscription);

const apiKeys = ref([]);
const showCreateKeyModal = ref(false);
const newKeyName = ref('');
const newCreatedKey = ref('');
const keyCopied = ref(false);

const scansUsed = computed(() => subscription.value?.scans_used || 0);
const scanLimit = computed(() => subscription.value?.scan_limit || 100);
const hardScansUsed = computed(() => subscription.value?.hard_scans_used || 0);
const hardScanLimit = computed(() => subscription.value?.hard_scan_limit || 0);

const usagePercentage = computed(() => {
  if (!scanLimit.value) return 0;
  return Math.min((scansUsed.value / scanLimit.value) * 100, 100);
});

const hardScanUsagePercentage = computed(() => {
  if (!hardScanLimit.value) return 0;
  return Math.min((hardScansUsed.value / hardScanLimit.value) * 100, 100);
});

const planName = computed(() => {
  const limit = scanLimit.value;
  if (limit <= 100) return 'Free Plan';
  if (limit <= 1000) return 'Pro Plan';
  return 'Enterprise Plan';
});

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const loadApiKeys = async () => {
  try {
    const response = await api.get('/api/profile/api-keys');
    apiKeys.value = response.data.api_keys;
  } catch (error) {
    console.error('Failed to load API keys:', error);
  }
};

const createApiKey = async () => {
  try {
    const response = await api.post('/api/profile/api-keys', {
      name: newKeyName.value || 'API Key'
    });

    newCreatedKey.value = response.data.api_key;
    newKeyName.value = '';

    uiStore.showToast({
      message: 'API key created successfully!',
      type: 'success'
    });

    await loadApiKeys();
  } catch (error) {
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to create API key',
      type: 'error'
    });
  }
};

const deleteApiKey = async (keyId) => {
  if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
    return;
  }

  try {
    await api.delete(`/api/profile/api-keys/${keyId}`);

    uiStore.showToast({
      message: 'API key deleted successfully',
      type: 'success'
    });

    await loadApiKeys();
  } catch (error) {
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to delete API key',
      type: 'error'
    });
  }
};

const copyKey = () => {
  navigator.clipboard.writeText(newCreatedKey.value);
  keyCopied.value = true;
  setTimeout(() => {
    keyCopied.value = false;
  }, 2000);
};

onMounted(async () => {
  await Promise.all([
    authStore.fetchUser(),
    loadApiKeys()
  ]);
});
</script>

<style scoped>
/* aligned with the public site design (light, violet accent, Plus Jakarta Sans) */
.profile-page {
  --violet: #2b82f0; --violet-hover: #1a6fdc; --ink: #1f2933; --muted: #6b7280;
  --line: rgba(31,41,51,0.08); --card: #ffffff; --pill: 999px;
  background: #f4f3f8; min-height: 100vh; padding: 44px 0 80px;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em; color: var(--ink);
}
.profile-page button, .profile-page input, .profile-page select { font-family: inherit; }
.page-container { max-width: 900px; margin: 0 auto; padding: 0 24px; }
.page-header { margin-bottom: 32px; }
.page-title { font-size: clamp(28px, 3.6vw, 42px); font-weight: 700; letter-spacing: -0.035em; color: var(--ink); margin: 0 0 8px; }
.page-subtitle { font-size: 16px; color: var(--muted); margin: 0; }

.section-card { background: var(--card); border: 1px solid var(--line); border-radius: 22px; box-shadow: 0 24px 56px -42px rgba(31,41,51,0.3); margin-bottom: 22px; overflow: hidden; }
.card-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 28px; border-bottom: 1px solid var(--line); }
.card-title { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0; letter-spacing: -0.01em; }
.card-content { padding: 26px 28px; }

.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
.info-item { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.info-label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; }
.info-value { font-size: 15.5px; font-weight: 600; color: var(--ink); margin: 0; }

.subscription-info { display: flex; flex-direction: column; gap: 20px; }
.sub-item { display: flex; flex-direction: column; gap: 6px; }
.sub-label { font-size: 13px; font-weight: 600; color: var(--muted); }
.sub-value { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0; }
.progress-bar { height: 10px; background: #eef1f6; border-radius: var(--pill); overflow: hidden; margin-top: 4px; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #5aa2ff, #2b82f0); border-radius: var(--pill); transition: width 0.5s ease; }
.progress-fill-premium { background: linear-gradient(90deg, #a78bfa, #7c5cff); }

.btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: var(--pill); border: none; background: var(--violet); color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 14px 26px -14px rgba(43,130,240,0.6); text-decoration: none; }
.btn-primary:hover { background: var(--violet-hover); transform: translateY(-2px); }
.btn-secondary { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: var(--pill); border: 1px solid var(--line); background: #fff; color: var(--ink); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; text-decoration: none; }
.btn-secondary:hover { border-color: var(--violet); color: var(--violet); transform: translateY(-2px); }

.api-note { display: flex; gap: 14px; align-items: flex-start; background: rgba(43,130,240,0.06); border: 1px solid rgba(43,130,240,0.18); border-radius: 14px; padding: 16px 18px; margin-bottom: 22px; color: var(--violet); }
.note-title { font-size: 14px; font-weight: 700; color: var(--ink); margin: 0 0 4px; }
.note-text { font-size: 13.5px; color: var(--muted); line-height: 1.55; margin: 0; }
.note-text code { background: rgba(43,130,240,0.1); color: var(--violet); padding: 1px 6px; border-radius: 5px; font-size: 12.5px; }

.api-keys-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 22px; }
.api-key-item { display: flex; align-items: center; justify-content: space-between; gap: 16px; background: #fbfbfd; border: 1px solid var(--line); border-radius: 14px; padding: 16px 18px; }
.key-name { font-size: 14.5px; font-weight: 700; color: var(--ink); }
.key-value { font-size: 13px; color: var(--muted); font-family: 'Fira Code', monospace; margin-top: 2px; }
.key-meta { font-size: 12px; color: #9aa3af; margin-top: 4px; }
.btn-delete { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: var(--pill); border: 1px solid rgba(239,68,68,0.25); background: #fff; color: #ef4444; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
.btn-delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }
.no-keys { color: var(--muted); font-size: 14px; padding: 8px 0 22px; }

.api-example { background: #161d27; border-radius: 14px; padding: 18px 20px; }
.example-title { font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.6); margin: 0 0 10px; }
.example-code { font-family: 'Fira Code', monospace; font-size: 12.5px; color: rgba(255,255,255,0.85); white-space: pre-wrap; word-break: break-all; margin: 0; line-height: 1.6; }

.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); }
.modal-content { background: #fff; border-radius: 22px; width: 100%; max-width: 460px; box-shadow: 0 40px 90px -40px rgba(31,41,51,0.5); overflow: hidden; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 22px 26px; border-bottom: 1px solid var(--line); }
.modal-header h3 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0; }
.btn-close { background: none; border: none; font-size: 26px; line-height: 1; color: var(--muted); cursor: pointer; }
.btn-close:hover { color: var(--ink); }
.modal-body { padding: 24px 26px; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group label { font-size: 13.5px; font-weight: 600; color: var(--ink); }
.form-input { width: 100%; padding: 12px 16px; border: 1px solid var(--line); border-radius: 12px; font-size: 15px; color: var(--ink); background: #fbfbfd; transition: all 0.2s ease; }
.form-input:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(43,130,240,0.12); background: #fff; }
.key-created-alert { margin-top: 18px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; padding: 16px; }
.alert-title { font-size: 14px; font-weight: 700; color: #b45309; margin: 0 0 4px; }
.alert-text { font-size: 13px; color: #92700e; margin: 0 0 12px; }
.key-display { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--line); border-radius: 10px; padding: 10px 12px; }
.key-display code { flex: 1; font-family: 'Fira Code', monospace; font-size: 12.5px; color: var(--ink); word-break: break-all; }
.btn-copy { padding: 7px 14px; border-radius: var(--pill); border: none; background: var(--violet); color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.btn-copy:hover { background: var(--violet-hover); }
.modal-footer { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 18px 26px; border-top: 1px solid var(--line); }

@media (max-width: 600px) {
  .info-grid { grid-template-columns: 1fr; }
  .card-header { flex-direction: column; align-items: flex-start; }
  .api-key-item { flex-direction: column; align-items: flex-start; }
}
</style>
